import React from 'react';
import { motion } from 'framer-motion';
import { Search, Code2, Rocket, ArrowRight } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Audit & System Architecture',
    desc: 'We analyze your operational bottlenecks, evaluate existing data sources, and design a custom software and AI blueprint.',
    image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80',
    icon: Search,
    accent: '#0284C7',
  },
  {
    num: '02',
    title: 'Precision Engineering & AI Build',
    desc: 'Our senior in-house engineers develop your product, train AI models on your private data, and integrate backend APIs.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80',
    icon: Code2,
    accent: '#4F46E5',
  },
  {
    num: '03',
    title: 'Production Launch & Scale',
    desc: 'We launch your system on cloud infrastructure, monitor performance, and provide continuous updates as you scale.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    icon: Rocket,
    accent: '#059669',
  },
];

const Process = () => {
  return (
    <section id="process" className="py-24 bg-[#FFFFFF] text-slate-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="mb-16 max-w-2xl">
          <div className="section-label mb-3">How We Deliver</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            From initial audit to <span className="text-sky-600">production code in weeks.</span>
          </h2>
        </div>

        {/* 3 Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, idx) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="bento-card-light flex flex-col justify-between group"
            >
              {/* Photo Header */}
              <div className="relative aspect-[16/10] overflow-hidden w-full">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 opacity-95"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                
                {/* Step Number Badge */}
                <div className="absolute top-4 left-4 font-mono font-bold text-xs text-sky-700 bg-white/90 backdrop-blur-md border border-slate-200 px-3 py-1 rounded-md shadow-sm">
                  STEP {step.num}
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between bg-white">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600 mb-4">
                    <step.icon size={20} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 font-normal">
                    {step.desc}
                  </p>
                </div>

                <div className="flex items-center gap-2 text-xs font-mono text-slate-500 uppercase tracking-wider pt-4 border-t border-slate-100">
                  <span>Phase {step.num} Execution</span>
                  <ArrowRight size={14} className="text-sky-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Process;
